import { api } from '@/config/api';

export interface CustomLimits {
  whatsappAccounts?: number;
  facebookAccounts?: number;
  googleAdsAccounts?: number;
  tiktokAccounts?: number;
  emailAccounts?: number;
  users?: number;
  contacts?: number;
  messagesPerMonth?: number;
  campaignsPerMonth?: number;
  aiCreditsPerMonth?: number;
  botFlows?: number;
}

/** How Company.industry (super-admin) lines up with the CRM profile's industry. */
export type IndustryLinkState = 'linked' | 'mismatch' | 'awaiting_onboarding' | 'crm_only' | 'unset';

export interface IndustryLink {
  /** What the CRM (AI bot, departments, packs) runs as. Null until onboarding. */
  profileIndustry: string | null;
  profileIndustryOther: string | null;
  state: IndustryLinkState;
}

export interface IndustryLinkRow extends IndustryLink {
  companyUid: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  status: Company['status'];
  companyIndustry: string | null;
}

export interface IndustryLinkReport {
  items: IndustryLinkRow[];
  counts: Record<IndustryLinkState, number>;
  needsReview: number;
}

export interface Company {
  _id: string; companyUid: string; name: string; slug: string; domain?: string;
  logoUrl?: string;
  status: 'active' | 'suspended' | 'expired' | 'trial'; planId: string;
  /** List endpoint: status after expiry — the stored field only flips on the tenant's next request. */
  effectiveStatus?: 'active' | 'suspended' | 'expired' | 'trial';
  /** List endpoint: trial end or plan expiry, whichever locks the tenant out first. */
  effectiveExpiresAt?: string | null;
  primaryAdminUid?: string;
  setupStatus?: 'pending' | 'ready' | 'needs_attention';
  setupWarnings?: string[];
  trialEndsAt?: string;
  defaultBotMode?: 'off' | 'flow' | 'ai_chat';
  expiresAt: string; createdAt: string; updatedAt?: string; userCount?: number;
  usesCustomPricing?: boolean;
  customLimits?: CustomLimits;
  customMonthlyPriceUsd?: number;
  /** Sidebar modules visible in SlightorCRM. undefined/null = all modules. */
  enabledModules?: string[] | null;
  /** Operator's own company — unlocks the Products module in the CRM. */
  isPlatformOwner?: boolean;
  industry?: string;
  /** Set here, read-only in the CRM. null = falls back to the country's default. */
  country?: string | null;
  defaultCurrency?: string | null;
  timezone?: string | null;
  /** Enriched by the list endpoint from the company_admin user. */
  adminEmail?: string;
  adminPhone?: string;
  /** Enriched by the list endpoint: messages/day for 30 days + last tenant activity. */
  activitySpark?: number[];
  lastActivityAt?: string | null;
  /** Detail endpoint: the CRM side of the industry link. */
  industryLink?: IndustryLink;
  /** List endpoint: the same, flattened per row. */
  profileIndustry?: string | null;
  industryState?: IndustryLinkState;
}

export type HealthLabel = 'healthy' | 'watch' | 'at_risk' | 'dormant';

export interface CompanyInsights {
  counts: { users: number; contacts: number; leads: number; deals: number; bookings: number; botFlows: number; broadcasts: number };
  channels: {
    whatsapp: { connected: number; active: number };
    meta: { connected: number };
    tiktok: { connected: number; active: number };
    google: { connected: number; active: number };
  };
  series: Array<{ date: string; inbound: number; outbound: number; leads: number }>;
  ai: { costUsd30d: number; generations30d: number };
  activity: { lastActivityAt: string | null; lastLoginAt: string | null; activeUsers30d: number };
  health: { score: number; label: HealthLabel; reasons: string[] };
}

export interface CompaniesSummary {
  total: number;
  byStatus: Record<string, number>;
  live: number;
  expiringSoon: number;
  usersTotal: number;
  signupsByDay: Array<{ date: string; count: number }>;
}

export interface ListCompaniesQuery { q?: string; status?: string; page?: number; limit?: number; }
export interface ListCompaniesResponse { items: Company[]; total: number; page: number; limit: number; }

export interface CreateCompanyPayload {
  name: string; slug: string; planId: string; expiresAt: string;
  industry?: string;
  adminFirstName: string; adminLastName: string; adminEmail: string; adminPassword: string;
  domain?: string; logoUrl?: string;
  usesCustomPricing?: boolean;
  customLimits?: CustomLimits;
  /** Set up the CRM from the industry family's blueprint (default true on the backend). */
  applyBlueprint?: boolean;
  country?: string;
  defaultCurrency?: string;
  timezone?: string;
  status?: 'active' | 'trial' | 'suspended';
  trialEndsAt?: string;
  enabledModules?: string[];
  defaultBotMode?: 'off' | 'flow' | 'ai_chat';
  includedAiCredits?: number;
}

export interface UpdateCompanyPayload {
  name?: string; domain?: string; logoUrl?: string;
  status?: string; expiresAt?: string;
  usesCustomPricing?: boolean;
  customLimits?: CustomLimits;
  enabledModules?: string[];
  isPlatformOwner?: boolean;
  industry?: string;
  /** '' clears back to the country default. */
  country?: string;
  defaultCurrency?: string;
  timezone?: string;
}

export interface CountryOption { code: string; label: string; currency: string; vatRatePct: number; vatInclusive: boolean; timezone: string }
export interface LocalizationOptions { countries: CountryOption[]; currencies: string[]; timezones: string[] }

export const companiesService = {
  list: async (q: ListCompaniesQuery): Promise<ListCompaniesResponse> =>
    (await api.get('/super_admin/companies', { params: q })).data,
  get: async (uid: string): Promise<Company> =>
    (await api.get(`/super_admin/companies/${uid}`)).data,
  summary: async (): Promise<CompaniesSummary> =>
    (await api.get('/super_admin/companies/summary')).data,
  insights: async (uid: string): Promise<CompanyInsights> =>
    (await api.get(`/super_admin/companies/${uid}/insights`)).data,
  create: async (p: CreateCompanyPayload): Promise<Company> =>
    (await api.post('/super_admin/companies', p)).data,
  update: async (uid: string, p: UpdateCompanyPayload): Promise<Company> =>
    (await api.patch(`/super_admin/companies/${uid}`, p)).data,
  remove: async (uid: string): Promise<void> => { await api.delete(`/super_admin/companies/${uid}`); },
  /** Countries with their default currency / VAT / timezone, plus every currency and timezone. */
  localizationOptions: async (): Promise<LocalizationOptions> =>
    (await api.get('/super_admin/companies/localization-options')).data,
  /** Super-admin vs CRM industry for every company. */
  industryLink: async (): Promise<IndustryLinkReport> =>
    (await api.get('/super_admin/companies/industry-link')).data,
  /** Make both records agree: keep the super-admin assignment or adopt the CRM's. */
  resolveIndustry: async (uid: string, keep: 'company' | 'profile'): Promise<Company> =>
    (await api.post(`/super_admin/companies/${uid}/industry/resolve`, { keep })).data,
  /** Uploads a logo image to ImageKit via the backend; returns the hosted URL. */
  uploadLogo: async (file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post('/super_admin/companies/upload-logo', form)).data;
  },
};
